import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ViewChild } from '@angular/core';

import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { FormBase } from '@shared/form-base.component';

import { SharedService } from '@services/shared.service';

import { RegisterModel, RegisterVerificationModel } from '@models/RegisterModel';
import { CustomerFindResultModel, CustomerWithoutRegisterModel } from '@shared/models/CustomerModel';
import { Country } from '@shared/models/Enums';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';

@Component({
    selector: 'app-register-new-form',
    templateUrl: './register-new-form.component.html',
    styleUrls: ['./register-new-form.component.scss']
})
export class RegisterNewFormComponent extends FormBase<RegisterModel> implements OnInit, OnChanges, OnDestroy {

    // Se setea cuando email viene del modal de recibir un Givi
    @Input() emailReceived: string = null;
    // Se setea cuando teléfono viene del modal de recibir un Givi
    @Input() phoneReceived: { number: string, countryCode: string, dialCode: string };

    //se setea la info del customer si se accede desde el checkout
    @Input() customerWithoutRegister: CustomerWithoutRegisterModel;

    // Se utiliza para manejar el estado de la verificación
    @Input() isVerificationSuccess = true;

    // Se setea cuando la verificación por email retorna duplicado
    @Input() emailDuplicated = false;
    // Se setea cuando la verificación por teléfono retorna duplicado
    @Input() customerPhoneDuplicated: CustomerFindResultModel;

    @Output() checkEmail = new EventEmitter<RegisterVerificationModel>();
    @Output() checkPhone = new EventEmitter<RegisterVerificationModel>();

    // Envía el email para que el Api envíe correo con el código de verificación
    @Output() verifyEmail = new EventEmitter<RegisterVerificationModel>();
    // Envía el teléfono para que el Api envíe sms con el código de verificación
    @Output() verifyPhone = new EventEmitter<RegisterVerificationModel>();

    @Input() inviteCode: string;
    @Input() receivedCountry: Country = Country.CR;

    // Si es true se piden nombre y apellido como campos requeridos
    @Input() requestName: boolean = false;

    @ViewChild('modalSelectedTypeVerify', { static: true }) modal: any;


    registerForm: UntypedFormGroup;

    // Evento para ejecutar en el componente hijo
    onReset = new Subject<boolean>()

    isVisiblePwd = false;

    codeAlert: string;

    requestAt: Date;

    showEmailOption = false;

    @Input() isVerificationWithPhone = true;

    // Se usa mostrar los segundos cuando se solicita el código de acceso
    now = new Date();
    requestedCodeAt: Date;
    nowTimerId = null;
    awaitSeconds = 10; // 20 sec
    sendedCode = false;
    sendEmailCount = 0;

    constructor(private router: Router,
        public sharedService: SharedService,
        private formBuilder: UntypedFormBuilder,
        private modalService: NgbModal) {
        super();
    }

    ngOnInit() {
        super.ngOnInit();

        this._formInit();
        this._refreshCurrentTime();
    }

    ngOnChanges(changes: SimpleChanges): void {

        for (const propName in changes) {
            if (propName === 'emailReceived') {
                const change = changes[propName];
                if (!change.isFirstChange() && (change.previousValue !== change.currentValue)) {

                    this._formInit();
                }
            }
            if (propName === 'phoneReceived') {
                const change = changes[propName];
                if (!change.isFirstChange() && (change.previousValue !== change.currentValue)) {

                    this._formInit();
                }
            }
            if (propName === 'isVerificationSuccess') {
                if (changes[propName].currentValue !== false) {

                    this._addCodeConfirmationValidator();
                }
            }
        }
    }

    ngOnDestroy(): void {

        if (this.nowTimerId) { clearInterval(this.nowTimerId); this.nowTimerId = null; }
    }


    onSubmit(): void {

        // if is invalid, show the form errors
        if (this.registerForm.invalid || this.emailDuplicated || this.customerPhoneDuplicated) {

            if (this.emailDuplicated) this.sharedService.showErrorAlert('Error', 'El email ya existe');
            if (this.customerPhoneDuplicated) this.sharedService.showErrorAlert('Error', 'El télefono ya existe');

            this.touched = false;
            return;
        }

        const registerModel = new RegisterModel();
        registerModel.email = this.email.value.toLowerCase().trim();
        registerModel.phoneCountry = this.phone.value?.dialCode;
        registerModel.phone = this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim();
        registerModel.codeConfirmation = this.codeConfirmation.value;
        registerModel.inviteCode = this.inviteCode;
        registerModel.requestAt = this.requestAt;
        registerModel.country = Number(this.country.value);

        if (this.requestName) {
            registerModel.name = this.name.value?.trim();
            registerModel.lastName = this.lastName.value?.trim();
        }

        this.onSave.emit(registerModel);
    }

    onCheckEmail() {

        if (this.invalidEmail) return;

        this.emailDuplicated = false;

        const registerModel = new RegisterVerificationModel({
            email: this.email.value.toLowerCase().trim(),
        });

        this.checkEmail.emit(registerModel);
    }

    onCheckPhone() {

        if (this.invalidPhone) return;

        this.sendedCode = true;

        this.customerPhoneDuplicated = null;

        const registerModel = new RegisterVerificationModel({
            phoneCountry: this.phone.value?.dialCode,
            phone: this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim(),
        });

        this.checkPhone.emit(registerModel);

        this.requestedCodeAt = new Date();
        setTimeout(() => this.sendedCode = false, (this.awaitSeconds * 1000));
    }

    onVerifyEmail(showAlert = false) {

        if (this.invalidEmail) return;

        this.requestAt = new Date();

        const registerModel = new RegisterVerificationModel({
            email: this.email.value?.trim(),
            requestAt: this.requestAt
        });

        this.verifyEmail.emit(registerModel);
        this.onReset?.next(true);

        this.sendEmailCount++;

        this._addCodeConfirmationValidator();

        this.isVerificationWithPhone = false;

        if (showAlert) {
            // Mostrar alerta
            this.codeAlert = 'Código de verificación reenviado';

            // Ocultar la alerta
            setTimeout(_ => this.codeAlert = null, 5000);
        }

        this.onClose();
    }

    onCountryChange(country) {

        if (Number(country.target.value) == Country.CR) this.phone.setValue({ number: '', countryCode: 'CR', dialCode: '+506' });
        else if (Number(country.target.value) == Country.PA) this.phone.setValue({ number: '', countryCode: 'PA', dialCode: '+507' });
        else this.phone.setValue({ number: '', countryCode: 'CR', dialCode: '+506' });
    }

    onSelectedTypeVerify(isPhone: boolean) {

    }

    openModalSelectedTypeVerify() {
        this.modalService.open(this.modal, {
            backdrop: 'static', keyboard: false,
            size: 'lg', centered: true, windowClass: 'modal-full'
        });
    }

    onClose(){

        this.modalService.dismissAll();

    }
    
    private async _refreshCurrentTime() {

        // First token
        this.now = new Date();

        // Refresh token each 1 second
        this.nowTimerId = setInterval(() => this.now = new Date(), 60);
    }

    formatTime(seconds: number): string {

        if (seconds > 0) {

            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;

            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        else {
            return '0:00';
        }
    }

    get codeAwaitSeconds(): number {

        let seconds = 0;

        if (this.requestedCodeAt) {

            const now = moment(this.now.toISOString());

            // expira en 30 segundos + 1 segundo de carga de UI
            const requestedCodeAt = new Date(this.requestedCodeAt);
            requestedCodeAt.setSeconds(requestedCodeAt?.getSeconds() + this.awaitSeconds + 1);
            const expiration = moment(requestedCodeAt?.toISOString());

            // Calcula los segundos entre la fecha actual y la fecha de solicitud del código.
            seconds = expiration.diff(now, 'seconds');
            if (seconds < 0) {
                seconds = 0;
            }
        }

        return seconds;
    }

    onVerifyPhone(showAlert = false) {

        if (this.invalidPhone) return;

        this.requestAt = new Date();
        this.sendedCode = true;

        const registerModel = new RegisterVerificationModel({
            phoneCountry: this.phone.value?.dialCode,
            phone: this.phone.value?.number?.replace(/ /g, '').replace(/-/g, '').trim(),
            requestAt: this.requestAt
        });

        this.verifyPhone.emit(registerModel);
        this.onReset?.next(true);

        this._addCodeConfirmationValidator();

        this.isVerificationWithPhone = true;

        if (showAlert) {
            // Mostrar alerta
            this.codeAlert = 'Código de verificación reenviado';

            // Ocultar la alerta
            setTimeout(_ => this.codeAlert = null, 10000); // 10 segundos

            // Cuando se reenvía se activa la opción de enviar por email
            this.showEmailOption = true;
        }

        this.onClose();

        // Se activa la opción de enviar por email después de un minuto
        setTimeout(_ => this.showEmailOption = true, 0); // 1 minuto

        // Oculta la opcion de reenviar codigo por SMS
        this.requestedCodeAt = new Date();
        setTimeout(() => this.sendedCode = false, (this.awaitSeconds * 1000));
    }

    onPhoneChange(ev: any) {

        // Limpiar mensaje de error
        this.customerPhoneDuplicated = null;
    }

    onContact() {

        window.open('http://wa.me/50670703344?text=¡Hola!%20Tengo%20una%20pregunta:%20');
    }

    onResetPassword() {

        // Navega a recuperar contraseña usando el teléfono por default
        this.router.navigate(['/recover'], { queryParams: { phone: '' } });
    }


    // Form

    isInvalid(): boolean {

        // Es invalido si el email ya existe
        if (this.emailDuplicated || this.customerPhoneDuplicated) return true;

        // Si los datos son validos compro var si la verificación se envío
        const invalid = this.registerForm.invalid;

        if (invalid && !this.isVerificationSuccess) {
            return true;
        } else if (this.isVerificationSuccess) {
            if (this.isVerificationWithPhone && this.phone.value?.number?.length) {
                return false;
            } else if (!this.isVerificationWithPhone && this.email.value?.length) {
                return false;
            }
            return true;
        }
    }

    isInvalidCode(): boolean {

        return this.codeConfirmation?.value?.length != 6;
    }

    get name() { return this._formGet(this.registerForm, 'name'); }
    get invalidName(): boolean { return this._isInvalid(this.registerForm, 'name', this.touched); }

    get lastName() { return this._formGet(this.registerForm, 'lastName'); }
    get invalidLastName(): boolean { return this._isInvalid(this.registerForm, 'lastName', this.touched); }

    get email() { return this._formGet(this.registerForm, 'email'); }
    get invalidEmail(): boolean {
        if (this.email && this.email.value) {
            this.email.setValue(this.email.value?.trim());
        }
        return this._isInvalid(this.registerForm, 'email', this.touched);
    }

    get phone() { return this._formGet(this.registerForm, 'phone'); }
    get invalidPhone(): boolean {
        if (this._isInvalid(this.registerForm, 'phone', this.touched)) {
            return true;
        }
        return false;
    }

    get country() { return this._formGet(this.registerForm, 'country'); }
    get invalidCountry(): boolean {
        if (this._isInvalid(this.registerForm, 'country', this.touched)) {
            return true;
        }
        return false;
    }

    get codeConfirmation() { return this._formGet(this.registerForm, 'codeConfirmation'); }
    get invalidCodeConfirmation(): boolean {
        return this._isInvalid(this.registerForm, 'codeConfirmation', this.touched);
    }

    private _formInit(): void {

        // Reset for hide form errors
        this.touched = true;

        let phone = { number: '', countryCode: 'CR', dialCode: '+506' };
        if (this.phoneReceived) {
            phone = this.phoneReceived;
        }

        let initCountry = this.customerWithoutRegister?.country != null ? this.customerWithoutRegister?.country : this.receivedCountry

        if (this.router.url == '/registro') initCountry = Country.None;

        this.registerForm = this.formBuilder.group(
            {
                name: new UntypedFormControl(
                    '',
                    this.requestName ? [Validators.required] : []
                ),
                lastName: new UntypedFormControl(
                    '',
                    this.requestName ? [Validators.required] : []
                ),
                email: new UntypedFormControl(
                    this.customerWithoutRegister?.email != '' ? this.customerWithoutRegister?.email : '',
                    [Validators.required, Validators.email]
                ),
                phone: [
                    phone,
                    [Validators.required, Validators.minLength(8)]
                ],
                country: new UntypedFormControl(
                    initCountry,
                    [Validators.required, Validators.min(1)]
                ),
                /*  password: new FormControl(
                    '',
                    [Validators.required, Validators.minLength(6), Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z]).{6,}')]
                ),
                passwordConfirmation: new FormControl(
                    '',
                    [Validators.required, Validators.minLength(6), Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z]).{6,}')]
                ), */
                codeConfirmation: new UntypedFormControl(
                    '',
                    []
                )
            },
            /*  {
                validator: MustMatch('password', 'passwordConfirmation')
            } */
        );
    }

    private _addCodeConfirmationValidator() {
        if (this.codeConfirmation) {
            this.codeConfirmation.setValue('');
            this.codeConfirmation.setValidators([Validators.required, Validators.minLength(6)]);
        } else {
            this.registerForm.addControl('codeConfirmation', this.formBuilder.control('', [Validators.required, Validators.minLength(6)]));
        }
    }

    // End Form
}
